import { extendObservable, action } from 'mobx';
import { browserHistory } from 'react-router';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import NotificationService from '../../../core/services/NotificationService';
import { isRequired } from '../../../core/validations';

export default class GrammarFormService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      grammarId: null,
    });
    this.form.validations = {
      name: [isRequired],
      spanishDescription: [isRequired],
      englishDescription: [isRequired],
      portugueseDescription: [isRequired],
    };
  }

  handleLoad = action((grammarId) => {
    this.form.reset();
    if (grammarId) {
      this.fetch
        .fetch({
          url: `/grammars/${grammarId}`,
        })
        .then(() => {
          if (this.fetch.data) {
            const data = this.fetch.data;
            this.form.setInitialValues({
              id: grammarId,
              name: this.fetch.data.name,
              spanishDescription: data.description.find(({ locale }) => locale === 'es')?.value,
              englishDescription: data.description.find(({ locale }) => locale === 'en')?.value,
              portugueseDescription: data.description.find(({ locale }) => locale === 'pt')?.value,
              images: [...(data.images || []).map((image) => ({ ...image, file: image.url }))],
              isDisabledForAIExercises: this.fetch.data.isDisabledForAIExercises,
            });
          }
        });
    } else {
      this.form.setInitialValues({ images: [] });
    }
    this.grammarId = grammarId;
  });

  handleSubmit = action(() => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    const grammarId = this.form.getValue('id');
    const description = [
      { locale: 'es', value: this.form.getValue('spanishDescription') },
      { locale: 'pt', value: this.form.getValue('portugueseDescription') },
      { locale: 'en', value: this.form.getValue('englishDescription') },
    ];
    const formValues = this.form.getValues();

    let bodyRequest;
    if (grammarId) {
      bodyRequest = {
        name: formValues.name,
        distributor: formValues.distributor,
        description,
        ...(formValues.images &&
          formValues.images.length && {
            images: formValues.images.map((image) => ({ order: image.order, url: image.url })),
          }),
        isDisabledForAIExercises: formValues.isDisabledForAIExercises,
      };
    } else {
      const formData = new FormData();
      formData.append('name', formValues.name);
      if (formValues.distributor) {
        formData.append('distributor', formValues.distributor);
      }
      description.forEach((desc, index) => {
        formData.append(`description[${index}][locale]`, desc.locale);
        formData.append(`description[${index}][value]`, desc.value);
      });
      if (formValues.images && formValues.images.length) {
        formValues.images.forEach((image, index) => {
          formData.append(`images-${index + 1}`, image.file);
        });
      }
      formData.append('isDisabledForAIExercises', formValues.isDisabledForAIExercises);
      bodyRequest = formData;
    }

    this.submit
      .fetch({
        method: grammarId ? 'put' : 'post',
        apiVersion: 'v2',
        url: grammarId ? `/grammars/${grammarId}` : '/grammars',
        body: bodyRequest,
      })
      .then(() => {
        if (this.submit.data) {
          const grammar = this.submit.data;
          this.grammarId = grammar.id;
          this.handleRefreshGrammarInfos(grammar);

          NotificationService.addNotification(`Grammar ${grammarId ? 'updated' : 'created'} successfully.`, 'success');
          if (!grammarId) {
            browserHistory.push('/grammars');
          }
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error ${grammarId ? 'updating' : 'creating'} grammar.`, 'error');
        }
      });
  });

  handleRefreshGrammarInfos = action((grammar) => {
    if (grammar) {
      this.form.reset();
      this.form.setInitialValues({
        id: grammar.id,
        name: grammar.name,
        distributor: grammar.distributor,
        spanishDescription: grammar.description.find(({ locale }) => locale === 'es')?.value,
        englishDescription: grammar.description.find(({ locale }) => locale === 'en')?.value,
        portugueseDescription: grammar.description.find(({ locale }) => locale === 'pt')?.value,
        images: [...(grammar.images || []).map((image) => ({ ...image, file: image.url }))],
        isDisabledForAIExercises: grammar.isDisabledForAIExercises,
      });
    }
  });

  handleAppendImage = action((id, file) => {
    if (!id || !file) return;
    const formData = new FormData();
    formData.append('image', file);
    this.submit
      .fetch({
        method: 'post',
        apiVersion: 'v2',
        url: `/grammars/${id}/image`,
        body: formData,
      })
      .then(() => {
        if (this.submit.data) {
          const grammar = this.submit.data;
          NotificationService.addNotification('Image successfully added.', 'success');
          this.handleRefreshGrammarInfos(grammar);
        }
      });
  });

  handleRemoveImage = action((id, order) => {
    if (!id || !order) return;
    this.submit
      .fetch({
        method: 'delete',
        apiVersion: 'v2',
        url: `/grammars/${id}/image/${order}`,
      })
      .then(() => {
        if (this.submit.data) {
          const grammar = this.submit.data;
          NotificationService.addNotification('Image successfully removed.', 'success');
          this.handleRefreshGrammarInfos(grammar);
        }
      });
  });

  handleAddFile = (file) => {
    if (!file) return;
    if (this.grammarId) {
      this.handleAppendImage(this.grammarId, file);
    } else {
      const images = [...this.form.getValue('images')];
      this.form.setValue('images', [...images, { order: images.length + 1, file }]);
    }
  };

  handleMove = (currentPosition, action) => {
    const images = this.form.getValue('images');
    if (images.length < 1) return;
    if (action === 'down') {
      if (!images[currentPosition]) return;
      images[currentPosition - 1].order += 1;
      images[currentPosition].order = currentPosition;
    }
    if (action === 'up') {
      if (!images[currentPosition - 2]) return;
      images[currentPosition - 1].order -= 1;
      images[currentPosition - 2].order += 1;
    }
    this.form.setValue(
      'images',
      images.sort((a, b) => a.order - b.order)
    );
  };

  handleRemove = (position) => {
    const images = this.form.getValue('images');
    if (this.grammarId) {
      this.handleRemoveImage(this.grammarId, position);
    } else {
      if (images.length <= 1) {
        return this.form.setValue('images', []);
      }
      const updatedImages = images.filter((image) => image.order !== position);
      this.form.setValue(
        'images',
        updatedImages.map((image, index) => {
          image.order = index + 1;
          return image;
        })
      );
    }
  };
}
